import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Phone, MessageCircle, BellRing, CalendarCheck2 } from "lucide-react";

const HEADLINE = [
  { text: "Build a Digital", accent: false },
  { text: "Presence That Works", accent: true },
  { text: "for Your Business", accent: false },
];

const RIBBON = [
  { step: "01", label: "Get Found" },
  { step: "02", label: "Get Enquiries" },
  { step: "03", label: "Automate" },
  { step: "04", label: "Grow" },
];

const FLOW_NODES = [
  { label: "Customer searches", y: 44 },
  { label: "Visits your website", y: 136 },
  { label: "Makes an enquiry", y: 228 },
  { label: "Becomes a customer", y: 320 },
];

function CircuitVisual() {
  return (
    <div className="glass-panel rounded-3xl p-6 sm:p-8 relative overflow-hidden" data-testid="hero-circuit-visual">
      <div className="flex items-center justify-between mb-6">
        <span className="micro-label">Enquiry Engine</span>
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-node" /> Live
        </span>
      </div>
      <svg viewBox="0 0 320 360" className="w-full h-auto" role="img" aria-label="Diagram: a customer enquiry flowing automatically from search to booked job">
        <line x1="60" y1="44" x2="60" y2="320" stroke="rgba(56,189,248,0.25)" strokeWidth="2" strokeDasharray="6 8" className="animate-trace-flow" />
        {FLOW_NODES.map((n, i) => (
          <g key={n.label}>
            <circle cx="60" cy={n.y} r="10" fill="#0B1528" stroke="#38BDF8" strokeWidth="2" className="animate-pulse-node" style={{ animationDelay: `${i * 0.5}s` }} />
            <circle cx="60" cy={n.y} r="3.5" fill="#0EA5E9" />
            <text x="88" y={n.y + 4} fill="#E2E8F0" fontSize="13" fontFamily="'Plus Jakarta Sans', sans-serif" fontWeight="600">{n.label}</text>
          </g>
        ))}
        <path d="M60 320 C 160 320 200 290 240 250" stroke="rgba(16,185,129,0.5)" strokeWidth="2" fill="none" strokeDasharray="5 7" className="animate-trace-flow" />
        <circle cx="246" cy="244" r="8" fill="none" stroke="#10B981" strokeWidth="2" />
        <circle cx="246" cy="244" r="3" fill="#10B981" />
      </svg>
      <div className="mt-6 rounded-2xl border border-sky-500/15 bg-navy-950/70 p-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Example automation</p>
        <div className="flex items-center gap-3 text-xs text-slate-300">
          <BellRing size={15} className="text-sky-400 shrink-0" />
          <span>New enquiry received</span>
          <ArrowRight size={13} className="text-slate-600 shrink-0" />
          <CalendarCheck2 size={15} className="text-emerald-400 shrink-0" />
          <span className="text-emerald-300">Instant reply sent</span>
        </div>
      </div>
      <div className="absolute -top-3 -right-3 glass-panel rounded-full px-4 py-2 flex items-center gap-2 animate-float-slow">
        <Phone size={13} className="text-sky-400" />
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300">Click-to-call</span>
      </div>
      <div className="absolute -bottom-3 left-6 glass-panel rounded-full px-4 py-2 flex items-center gap-2 animate-float-slow" style={{ animationDelay: "1.8s" }}>
        <MessageCircle size={13} className="text-sky-400" />
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300">WhatsApp ready</span>
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative grid-bg overflow-hidden pt-[72px]">
      <div className="pointer-events-none absolute -top-40 right-[-10%] w-[560px] h-[560px] rounded-full bg-sky-500/10 blur-[140px]" />
      <div className="section-shell relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center pt-14 pb-16 sm:pt-20 sm:pb-24">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="micro-label sm:flex sm:items-center sm:gap-3 !tracking-[0.14em] sm:!tracking-[0.22em]"
            data-testid="hero-tagline"
          >
            <span className="hidden sm:inline-block w-8 h-px bg-sky-400" />
            Get Found · Get Enquiries · Automate Growth
          </motion.p>
          <h1 className="mt-6 font-heading font-black tracking-tight leading-[1.1] sm:leading-[1.06] text-[28px] sm:text-5xl lg:text-6xl text-white" data-testid="hero-headline">
            {HEADLINE.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  className={`block ${line.accent ? "text-gradient-sky" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-slate-400 max-w-xl"
            data-testid="hero-subtext"
          >
            Professional websites, Google visibility and smart automation designed to help local
            businesses get found, capture enquiries and turn more prospects into customers.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a href="#contact" data-testid="hero-cta-consultation" className="btn-primary">
              Get a Free Consultation <ArrowRight size={16} strokeWidth={2.5} />
            </a>
            <a href="#services" data-testid="hero-cta-services" className="btn-ghost">
              View Our Services <ArrowDown size={16} strokeWidth={2.5} />
            </a>
          </motion.div>
          <motion.ol
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-sky-500/15 bg-sky-500/10"
            data-testid="hero-journey-ribbon"
          >
            {RIBBON.map((s, i) => (
              <li key={s.step} className="relative bg-navy-950/95 px-4 py-4 sm:py-5">
                <span className="font-mono text-[10px] tracking-[0.2em] text-sky-500">{s.step}</span>
                <p className="mt-1 font-heading font-bold text-sm sm:text-base text-white uppercase tracking-wide">{s.label}</p>
                {i < RIBBON.length - 1 && (
                  <ArrowRight size={14} className="hidden sm:block absolute top-1/2 -right-2 -translate-y-1/2 text-sky-400 z-10" />
                )}
              </li>
            ))}
          </motion.ol>
        </motion.div>
        <motion.div
          style={{ y: yVisual }}
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 py-6"
        >
          <CircuitVisual />
        </motion.div>
      </div>
    </section>
  );
}
